import { useMemo, useState } from 'react';

// Shared course selector used across the Audio Course tabs (Build, Library, Store).
// A course-id is "<lang>_<edition>" and its label is "Language — Edition"; one language can
// carry several courses (Georgian → Michael + Travel). These helpers split the flat LANGS list
// into a language → course cascade so the UI stays usable as course counts grow per language.
export type Course = { code: string; name: string };

export const langDisplayOf = (name: string) => name.split('—')[0]!.trim();
export const editionOf = (name: string) => { const p = name.split('—'); return p.length > 1 ? p.slice(1).join('—').trim() : name; };
export const languagesOf = (langs: Course[]) => [...new Set(langs.map((l) => langDisplayOf(l.name)))].sort((a, b) => a.localeCompare(b));
export const coursesOf = (langs: Course[], language: string) => langs.filter((l) => langDisplayOf(l.name) === language);
export const languageOfCourse = (langs: Course[], code: string) => { const e = langs.find((l) => l.code === code); return e ? langDisplayOf(e.name) : ''; };

// Full cascade with an explicit Load button — nothing loads until the operator commits a
// language + course + level. Used where loading is heavy (Build) or starts empty (Library).
export default function CoursePicker({
  langs, levels, current, onLoad, loadLabel = 'Load course', busy = false,
}: {
  langs: Course[];
  levels: string[];
  current?: { course: string; level: string };   // currently-loaded selection, to seed the dropdowns + show a hint
  onLoad: (course: string, level: string) => void;
  loadLabel?: string;
  busy?: boolean;
}) {
  const [selLanguage, setSelLanguage] = useState(() => (current?.course ? languageOfCourse(langs, current.course) : ''));
  const [selCourse, setSelCourse] = useState(current?.course || '');
  const [selLevel, setSelLevel] = useState(current?.level || levels[0] || 'A1');
  const languages = useMemo(() => languagesOf(langs), [langs]);
  const courses = useMemo(() => coursesOf(langs, selLanguage), [langs, selLanguage]);

  const pickLanguage = (language: string) => {
    setSelLanguage(language);
    const cs = coursesOf(langs, language);
    setSelCourse(cs.length === 1 ? cs[0]!.code : '');   // auto-pick when the language has a single course
  };
  const loadedLabel = langs.find((l) => l.code === current?.course)?.name;

  return (
    <section className="flex flex-wrap items-end gap-3">
      <label className="block">
        <span className="text-xs text-gray-400">Language</span>
        <select value={selLanguage} onChange={(e) => pickLanguage(e.target.value)}
          className="mt-1 w-full sm:w-56 rounded bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm text-gray-100 focus:border-accent-teal focus:outline-none">
          <option value="">— choose a language —</option>
          {languages.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
      </label>
      <label className="block">
        <span className="text-xs text-gray-400">Course</span>
        <select value={selCourse} onChange={(e) => setSelCourse(e.target.value)} disabled={!selLanguage}
          className="mt-1 w-full sm:w-56 rounded bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm text-gray-100 focus:border-accent-teal focus:outline-none disabled:opacity-50">
          <option value="">{selLanguage ? '— choose a course —' : '— pick a language first —'}</option>
          {courses.map((c) => <option key={c.code} value={c.code}>{editionOf(c.name)}</option>)}
        </select>
      </label>
      <label className="block">
        <span className="text-xs text-gray-400">Level</span>
        <select value={selLevel} onChange={(e) => setSelLevel(e.target.value)}
          className="mt-1 w-full sm:w-28 rounded bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm text-gray-100 focus:border-accent-teal focus:outline-none">
          {levels.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
      </label>
      <button onClick={() => selCourse && onLoad(selCourse, selLevel)} disabled={!selCourse || busy}
        className="rounded bg-accent-teal/20 border border-accent-teal/40 text-accent-teal px-4 py-2 text-sm font-medium hover:bg-accent-teal/30 disabled:opacity-50">
        {busy ? 'Loading…' : loadLabel}
      </button>
      {current?.course && <span className="text-xs text-gray-500 pb-2">Loaded: <b className="text-gray-300">{loadedLabel || current.course}</b> · {current.level}</span>}
    </section>
  );
}
